/**
 * Privilege Protection Hooks
 *
 * Hooks for managing attorney-client privilege and work product protection.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Json } from '@/integrations/supabase/types';
import { trackSupabaseError } from '@/lib/analytics/errorHandler';

// Types
export interface AttorneySupervision {
  id: string;
  user_id: string;
  attorney_advisor_id: string;
  supervision_type: 'full_case' | 'limited_scope';
  scope_description?: string;
  supervision_started_at: string;
  supervision_ended_at?: string;
  attorney_acknowledged_supervision: boolean;
  attorney_acknowledged_at?: string;
  attorney_bar_number?: string;
  attorney_jurisdiction?: string;
  retainer_covers_platform: boolean;
  retainer_reference?: string;
  platform_integration_acknowledged: boolean;
  kovel_acknowledgment: boolean;
  kovel_acknowledged_at?: string;
  // Non-testifying expert designation for FRCP 26(b)(4)(D) protection
  non_testifying_expert_designated?: boolean;
  non_testifying_expert_designated_at?: string;
  // Retention agreement (optional formal agreement)
  retention_agreement_signed?: boolean;
  retention_agreement_signed_at?: string;
  retention_agreement_url?: string;
  attorney_electronic_signature?: string | null;
  kovel_acknowledgment_version?: string | null;
  created_at: string;
  // Joined data
  attorney?: {
    id: string;
    firm_name?: string;
    profiles?: {
      full_name?: string;
      email?: string;
    };
  };
}

export interface PrivilegeAssertion {
  id: string;
  user_id: string;
  assertion_scope: 'specific_item' | 'category' | 'date_range' | 'all_data';
  resource_type?: string;
  resource_id?: string;
  category?: string;
  date_range_start?: string;
  date_range_end?: string;
  privilege_type: 'attorney_client' | 'work_product' | 'common_interest' | 'joint_defense';
  privilege_basis: string;
  privilege_description?: string;
  asserted_by_user_id?: string;
  asserted_by_attorney_id?: string;
  asserted_at: string;
  attorney_verified: boolean;
  attorney_verified_by?: string;
  attorney_verified_at?: string;
  status: 'active' | 'waived' | 'challenged' | 'upheld';
  created_at: string;
}

export type PrivilegeClassification =
  | 'attorney_privileged'
  | 'work_product'
  | 'common_interest'
  | 'confidential'
  | 'user_content';

export interface PrivilegeContext {
  hasAttorneySupervision: boolean;
  supervisingAttorneyId?: string;
  supervisionId?: string;
  defaultClassification: PrivilegeClassification;
}

/**
 * Hook to check if user has active attorney supervision
 */
export function useAttorneySupervision() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['attorney-supervision', user?.id],
    queryFn: async (): Promise<AttorneySupervision | null> => {
      const { data, error } = await supabase
        .from('attorney_supervision')
        .select(`
          *,
          attorney:advisor_profiles!attorney_advisor_id (
            id,
            firm_name,
            user_id
          )
        `)
        .eq('user_id', user!.id)
        .is('supervision_ended_at', null)
        .eq('attorney_acknowledged_supervision', true)
        .order('supervision_started_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        trackSupabaseError('select', 'attorney_supervision', error);
        throw error;
      }

      // If we have attorney data, fetch the profile separately
      if (data?.attorney?.user_id) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('full_name, email')
          .eq('id', data.attorney.user_id)
          .single();

        if (profileData) {
          return {
            ...data,
            attorney: {
              ...data.attorney,
              profiles: profileData,
            },
          };
        }
      }

      return data;
    },
    enabled: !!user,
  });
}

/**
 * Hook to get all attorney supervisions (including inactive)
 */
export function useAllAttorneySupervisions() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['attorney-supervisions-all', user?.id],
    queryFn: async (): Promise<AttorneySupervision[]> => {
      const { data, error } = await supabase
        .from('attorney_supervision')
        .select(`
          *,
          attorney:advisor_profiles!attorney_advisor_id (
            id,
            firm_name,
            user_id
          )
        `)
        .eq('user_id', user!.id)
        .order('supervision_started_at', { ascending: false });

      if (error) {
        trackSupabaseError('select', 'attorney_supervision', error);
        throw error;
      }
      if (!data) return [];

      // Fetch profiles for all unique attorney user_ids
      const attorneyUserIds = [...new Set(data.map(d => d.attorney?.user_id).filter(Boolean))];
      if (attorneyUserIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .in('id', attorneyUserIds);

        const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

        return data.map(item => ({
          ...item,
          attorney: item.attorney ? {
            ...item.attorney,
            profiles: profileMap.get(item.attorney.user_id),
          } : undefined,
        }));
      }

      return data;
    },
    enabled: !!user,
  });
}

/**
 * The clients an attorney supervises, for the attorney's own privilege page (2026-10-01). The Kovel
 * acknowledgment form existed but nothing rendered it; this is its entry point. RLS lets an attorney read
 * supervision rows where attorney_advisor_id is their advisor profile.
 */
export interface SupervisedClient extends AttorneySupervision { clientName: string }

export function useSupervisedClients(advisorProfileId: string | null | undefined) {
  return useQuery({
    queryKey: ['supervised-clients', advisorProfileId],
    queryFn: async (): Promise<SupervisedClient[]> => {
      const { data, error } = await supabase
        .from('attorney_supervision')
        .select('*')
        .eq('attorney_advisor_id', advisorProfileId!)
        .is('supervision_ended_at', null)
        .order('supervision_started_at', { ascending: false });
      if (error) {
        trackSupabaseError('select', 'attorney_supervision', error);
        throw error;
      }
      const rows = (data ?? []) as AttorneySupervision[];
      const ids = [...new Set(rows.map((r) => r.user_id))];
      const names = new Map<string, string>();
      if (ids.length > 0) {
        const { data: profiles } = await supabase.from('profiles').select('id, full_name').in('id', ids);
        for (const pr of profiles ?? []) names.set(pr.id, pr.full_name ?? '');
      }
      return rows.map((r) => ({ ...r, clientName: names.get(r.user_id) || 'Client' }));
    },
    enabled: !!advisorProfileId,
  });
}

/**
 * Hook to get the current privilege context for the user
 */
export function usePrivilegeContext(): PrivilegeContext {
  const { data: supervision, isLoading } = useAttorneySupervision();

  if (isLoading) {
    // Return safe defaults while loading
    return {
      hasAttorneySupervision: false,
      defaultClassification: 'work_product',
    };
  }

  if (supervision) {
    return {
      hasAttorneySupervision: true,
      supervisingAttorneyId: supervision.attorney_advisor_id,
      supervisionId: supervision.id,
      defaultClassification: 'attorney_privileged',
    };
  }

  return {
    hasAttorneySupervision: false,
    defaultClassification: 'work_product',
  };
}

/**
 * Hook for attorney to acknowledge supervision
 */
export function useAcknowledgeSupervision() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      supervisionId,
      barNumber,
      jurisdiction,
      retainerCovers,
      retainerReference,
    }: {
      supervisionId: string;
      barNumber?: string;
      jurisdiction?: string;
      retainerCovers: boolean;
      retainerReference?: string;
    }) => {
      const { data, error } = await supabase
        .from('attorney_supervision')
        .update({
          attorney_acknowledged_supervision: true,
          attorney_acknowledged_at: new Date().toISOString(),
          attorney_bar_number: barNumber,
          attorney_jurisdiction: jurisdiction,
          retainer_covers_platform: retainerCovers,
          retainer_reference: retainerReference,
          platform_integration_acknowledged: true,
          platform_integration_acknowledged_at: new Date().toISOString(),
          kovel_acknowledgment: true,
          kovel_acknowledged_at: new Date().toISOString(),
        })
        .eq('id', supervisionId)
        .select()
        .single();

      if (error) {
        trackSupabaseError('update', 'attorney_supervision', error);
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attorney-supervision'] });
      queryClient.invalidateQueries({ queryKey: ['attorney-supervisions-all'] });
    },
  });
}

/**
 * Hook to create a new attorney supervision relationship
 */
export function useCreateSupervision() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      attorneyAdvisorId,
      supervisionType = 'full_case',
      scopeDescription,
    }: {
      attorneyAdvisorId: string;
      supervisionType?: 'full_case' | 'limited_scope';
      scopeDescription?: string;
    }) => {
      const { data, error } = await supabase
        .from('attorney_supervision')
        .insert({
          user_id: user!.id,
          attorney_advisor_id: attorneyAdvisorId,
          supervision_type: supervisionType,
          scope_description: scopeDescription,
        })
        .select()
        .single();

      if (error) {
        trackSupabaseError('insert', 'attorney_supervision', error);
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attorney-supervision'] });
      queryClient.invalidateQueries({ queryKey: ['attorney-supervisions-all'] });
    },
  });
}

/**
 * Hook to end attorney supervision
 */
export function useEndSupervision() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (supervisionId: string) => {
      const { data, error } = await supabase
        .from('attorney_supervision')
        .update({
          supervision_ended_at: new Date().toISOString(),
        })
        .eq('id', supervisionId)
        .select()
        .single();

      if (error) {
        trackSupabaseError('update', 'attorney_supervision', error);
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attorney-supervision'] });
      queryClient.invalidateQueries({ queryKey: ['attorney-supervisions-all'] });
    },
  });
}

/**
 * Hook to get privilege assertions
 */
export function usePrivilegeAssertions() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['privilege-assertions', user?.id],
    queryFn: async (): Promise<PrivilegeAssertion[]> => {
      const { data, error } = await supabase
        .from('privilege_assertions')
        .select('*')
        .eq('user_id', user!.id)
        .eq('status', 'active')
        .order('asserted_at', { ascending: false });

      if (error) {
        trackSupabaseError('select', 'privilege_assertions', error);
        throw error;
      }
      // Cast through unknown to handle database enum strings
      return (data || []) as unknown as PrivilegeAssertion[];
    },
    enabled: !!user,
  });
}

/**
 * Hook to create a privilege assertion
 */
export function useCreatePrivilegeAssertion() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      assertionScope,
      resourceType,
      resourceId,
      category,
      dateRangeStart,
      dateRangeEnd,
      privilegeType,
      privilegeBasis,
      privilegeDescription,
    }: {
      assertionScope: 'specific_item' | 'category' | 'date_range' | 'all_data';
      resourceType?: string;
      resourceId?: string;
      category?: string;
      dateRangeStart?: string;
      dateRangeEnd?: string;
      privilegeType: 'attorney_client' | 'work_product' | 'common_interest' | 'joint_defense';
      privilegeBasis: string;
      privilegeDescription?: string;
    }) => {
      const { data, error } = await supabase
        .from('privilege_assertions')
        .insert({
          user_id: user!.id,
          assertion_scope: assertionScope,
          resource_type: resourceType,
          resource_id: resourceId,
          category,
          date_range_start: dateRangeStart,
          date_range_end: dateRangeEnd,
          privilege_type: privilegeType,
          privilege_basis: privilegeBasis,
          privilege_description: privilegeDescription,
          asserted_by_user_id: user!.id,
        })
        .select()
        .single();

      if (error) {
        trackSupabaseError('insert', 'privilege_assertions', error);
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['privilege-assertions'] });
    },
  });
}

/**
 * Hook to log privileged access (call this when accessing sensitive data)
 */
export function useLogPrivilegedAccess() {
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      resourceType,
      resourceId,
      resourceName,
      accessType,
      privilegeClassification,
      context,
    }: {
      resourceType: string;
      resourceId: string;
      resourceName?: string;
      accessType: 'view' | 'create' | 'edit' | 'delete' | 'export' | 'share' | 'download';
      privilegeClassification?: PrivilegeClassification;
      context?: Record<string, unknown>;
    }) => {
      const { data, error } = await supabase.rpc('log_privileged_access', {
        p_resource_type: resourceType,
        p_resource_id: resourceId,
        p_resource_name: resourceName || null,
        p_accessed_by_user_id: user!.id,
        p_accessed_by_advisor_id: null,
        p_access_type: accessType,
        p_privilege_classification: privilegeClassification || 'user_content',
        p_ip_address: null,
        p_user_agent: navigator.userAgent,
        p_context: (context || {}) as Json,
      });

      if (error) {
        trackSupabaseError('select', 'log_privileged_access', error);
        throw error;
      }
      return data;
    },
  });
}

/**
 * Hook to determine privilege classification for new content
 */
export function useDeterminePrivilegeClassification() {
  const { user } = useAuth();
  const { hasAttorneySupervision } = usePrivilegeContext();

  return async (contentType: string): Promise<PrivilegeClassification> => {
    const { data, error } = await supabase.rpc('determine_privilege_classification', {
      p_user_id: user!.id,
      p_content_type: contentType,
      p_has_attorney: hasAttorneySupervision,
    });

    if (error) {
      trackSupabaseError('select', 'determine_privilege_classification', error);
      // Fallback classification
      return hasAttorneySupervision ? 'attorney_privileged' : 'work_product';
    }

    return data as PrivilegeClassification;
  };
}

/**
 * Human-readable labels for privilege classifications
 */
export const PRIVILEGE_LABELS: Record<PrivilegeClassification, string> = {
  attorney_privileged: 'Attorney-Client Privileged',
  work_product: 'Work Product',
  common_interest: 'Common Interest Protected',
  confidential: 'Confidential',
  user_content: 'User Content',
};

/**
 * Get privilege badge color class
 */
export function getPrivilegeBadgeClass(classification: PrivilegeClassification): string {
  switch (classification) {
    case 'attorney_privileged':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'work_product':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'common_interest':
      return 'bg-indigo-100 text-indigo-800 border-indigo-200';
    case 'confidential':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'user_content':
      return 'bg-slate-100 text-slate-800 border-slate-200';
    default:
      return 'bg-slate-100 text-slate-800 border-slate-200';
  }
}
